import React, { useState, useEffect } from "react";
import {
    Box,
    Typography,
    Button,
    Card,
    CardContent,
    TextField,
    Alert,
    CircularProgress,
    Avatar,
    Select,
    MenuItem,
    FormControl,
    InputLabel,
} from "@mui/material";
import SettingsIcon from "@mui/icons-material/Settings";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import { updateProfile } from "../../api/api";
import { getStorageUrl } from "../../config/config";

/**
 * Complete Your Registration form shown after free trial email verification.
 * On submit, updates profile via API and redirects to service-management (skip profile edit).
 */
// Map of common country codes to phone dial codes
const COUNTRY_PHONE_PREFIXES = {
    NO: { country: "Norway", phone: "+47" },
    SE: { country: "Sweden", phone: "+46" },
    DK: { country: "Denmark", phone: "+45" },
    FI: { country: "Finland", phone: "+358" },
    IS: { country: "Iceland", phone: "+354" },
    US: { country: "United States", phone: "+1" },
    GB: { country: "United Kingdom", phone: "+44" },
    DE: { country: "Germany", phone: "+49" },
    FR: { country: "France", phone: "+33" },
    ES: { country: "Spain", phone: "+34" },
    IT: { country: "Italy", phone: "+39" },
    NL: { country: "Netherlands", phone: "+31" },
    BE: { country: "Belgium", phone: "+32" },
    AT: { country: "Austria", phone: "+43" },
    CH: { country: "Switzerland", phone: "+41" },
    PL: { country: "Poland", phone: "+48" },
    PT: { country: "Portugal", phone: "+351" },
    IE: { country: "Ireland", phone: "+353" },
    CA: { country: "Canada", phone: "+1" },
    AU: { country: "Australia", phone: "+61" },
    NZ: { country: "New Zealand", phone: "+64" },
    IN: { country: "India", phone: "+91" },
    JP: { country: "Japan", phone: "+81" },
    KR: { country: "South Korea", phone: "+82" },
    CN: { country: "China", phone: "+86" },
    BR: { country: "Brazil", phone: "+55" },
    MX: { country: "Mexico", phone: "+52" },
    ZA: { country: "South Africa", phone: "+27" },
    AE: { country: "United Arab Emirates", phone: "+971" },
    SG: { country: "Singapore", phone: "+65" },
    PH: { country: "Philippines", phone: "+63" },
    TH: { country: "Thailand", phone: "+66" },
    MY: { country: "Malaysia", phone: "+60" },
    ID: { country: "Indonesia", phone: "+62" },
    PK: { country: "Pakistan", phone: "+92" },
    BD: { country: "Bangladesh", phone: "+880" },
    NG: { country: "Nigeria", phone: "+234" },
    EG: { country: "Egypt", phone: "+20" },
    TR: { country: "Turkey", phone: "+90" },
    RU: { country: "Russia", phone: "+7" },
    UA: { country: "Ukraine", phone: "+380" },
    RO: { country: "Romania", phone: "+40" },
    CZ: { country: "Czech Republic", phone: "+420" },
    GR: { country: "Greece", phone: "+30" },
    HU: { country: "Hungary", phone: "+36" },
    HR: { country: "Croatia", phone: "+385" },
    SK: { country: "Slovakia", phone: "+421" },
    BG: { country: "Bulgaria", phone: "+359" },
    LT: { country: "Lithuania", phone: "+370" },
    LV: { country: "Latvia", phone: "+371" },
    EE: { country: "Estonia", phone: "+372" },
    SI: { country: "Slovenia", phone: "+386" },
    CL: { country: "Chile", phone: "+56" },
    AR: { country: "Argentina", phone: "+54" },
    CO: { country: "Colombia", phone: "+57" },
    PE: { country: "Peru", phone: "+51" },
    VE: { country: "Venezuela", phone: "+58" },
    SA: { country: "Saudi Arabia", phone: "+966" },
    IL: { country: "Israel", phone: "+972" },
    KE: { country: "Kenya", phone: "+254" },
    GH: { country: "Ghana", phone: "+233" },
    TZ: { country: "Tanzania", phone: "+255" },
    LK: { country: "Sri Lanka", phone: "+94" },
};

const CompleteRegistrationForm = ({ initialData, onSuccess, onError }) => {
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState("");
    const [formData, setFormData] = useState({
        companyName: "",
        email: initialData?.email || "",
        firstName: "",
        lastName: "",
        phoneCode: "+47",
        phoneNumber: "",
        country: "",
    });
    const [logoFile, setLogoFile] = useState(null);
    const [logoFileName, setLogoFileName] = useState("");
    const [logoPreview, setLogoPreview] = useState(null);

    const branchId = localStorage.getItem("branch_id");

    // Auto-detect country from IP on mount
    useEffect(() => {
        const detectCountry = async () => {
            try {
                const res = await fetch("https://ipapi.co/json/");
                if (!res.ok) return;
                const data = await res.json();
                const countryCode = data.country_code; // e.g. "NO"
                const info = COUNTRY_PHONE_PREFIXES[countryCode];
                const countryName = info?.country || data.country_name || "";
                const phonePrefix = info?.phone || "";
                setFormData((prev) => ({
                    ...prev,
                    country: prev.country || countryName,
                    phoneCode: phonePrefix || prev.phoneCode,
                }));
            } catch {
                // Silently ignore — user can fill in manually
            }
        };
        detectCountry();
    }, []);

    // Existing logo from initialData (e.g. already has logo from backend)
    const existingLogoUrl = initialData?.logo
        ? getStorageUrl(`/storage/logos/${initialData.logo}`)
        : null;

    useEffect(() => {
        if (!logoFile) {
            setLogoPreview(null);
            return;
        }
        const url = URL.createObjectURL(logoFile);
        setLogoPreview(url);
        return () => URL.revokeObjectURL(url);
    }, [logoFile]);

    const handleChange = (field) => (e) => {
        const value = e.target.value;
        setFormData((prev) => ({ ...prev, [field]: value }));
        setError("");
    };

    const handleLogoChange = (e) => {
        const file = e.target.files?.[0];
        if (file) {
            if (!file.type.startsWith("image/")) {
                return;
            }
            setLogoFile(file);
            setLogoFileName(file.name);
        }
    };

    const logoDisplayUrl = logoPreview || existingLogoUrl;

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError("");
        if (!branchId) {
            setError("Session expired. Please log in again.");
            return;
        }
        if (!formData.email?.trim()) {
            setError("Email is required.");
            return;
        }

        setSaving(true);
        try {
            const formDataToSend = new FormData();
            formDataToSend.append("branch_id", String(branchId));
            formDataToSend.append("email", formData.email.trim());
            // Only send fields that the user has filled in
            if (formData.companyName?.trim()) {
                formDataToSend.append("branch_name", formData.companyName.trim());
            }
            const fullName = [formData.firstName.trim(), formData.lastName.trim()].filter(Boolean).join(" ");
            if (fullName) {
                formDataToSend.append("user_name", fullName);
            }
            const phoneNumber = (formData.phoneNumber || "").trim();
            if (phoneNumber.length >= 4) {
                const phone = (formData.phoneCode || "") + " " + phoneNumber;
                formDataToSend.append("phone", phone.trim());
            }
            if (formData.country?.trim()) {
                formDataToSend.append("country", formData.country.trim());
            }
            if (logoFile) {
                formDataToSend.append("logo", logoFile);
            }

            const response = await updateProfile(formDataToSend);

            if (response?.success) {
                const updatedUser = {
                    ...JSON.parse(localStorage.getItem("user") || "{}"),
                    branch_name: response.user?.branch_name,
                    user_name: response.user?.user_name,
                    email: response.user?.email,
                    phone: response.user?.phone,
                    logo: response.user?.logo,
                    is_free_trial: response.user?.is_free_trial ?? true,
                };
                localStorage.setItem("user", JSON.stringify(updatedUser));
                if (response.user?.branch_name) {
                    localStorage.setItem("branch_name", response.user.branch_name);
                }
                onSuccess?.(response);
            } else {
                setError(response?.message || "Failed to create account. Please try again.");
                onError?.(response);
            }
        } catch (err) {
            const msg =
                err.response?.data?.message ||
                err.message ||
                "An error occurred. Please try again.";
            setError(msg);
            onError?.(err);
        } finally {
            setSaving(false);
        }
    };

    return (
        <Card
            sx={{
                maxWidth: 520,
                width: "100%",
                boxShadow: 3,
                borderRadius: 2,
                overflow: "visible",
            }}
        >
            <CardContent sx={{ p: { xs: 3, sm: 4 } }}>
                <Box
                    sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                        mb: 3,
                    }}
                >
                    <SettingsIcon sx={{ color: "primary.main", fontSize: 28 }} />
                    <Typography
                        variant="h5"
                        component="h1"
                        sx={{
                            fontWeight: 600,
                            color: "primary.main",
                        }}
                    >
                        Complete Your Registration
                    </Typography>
                </Box>

                {error && (
                    <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError("")}>
                        {error}
                    </Alert>
                )}

                <Box component="form" onSubmit={handleSubmit} noValidate>
                    <TextField
                        fullWidth
                        type="email"
                        label="Email"
                        value={formData.email}
                        margin="normal"
                        autoComplete="email"
                        InputProps={{ readOnly: true }}
                        sx={{ "& .MuiInputBase-input": { color: "text.secondary" } }}
                    />
                    <TextField
                        fullWidth
                        label="Company Name"
                        value={formData.companyName}
                        onChange={handleChange("companyName")}
                        margin="normal"
                        autoComplete="off"
                    />
                    <TextField
                        fullWidth
                        label="First Name"
                        value={formData.firstName}
                        onChange={handleChange("firstName")}
                        margin="normal"
                        autoComplete="off"
                    />
                    <TextField
                        fullWidth
                        label="Last Name"
                        value={formData.lastName}
                        onChange={handleChange("lastName")}
                        margin="normal"
                        autoComplete="off"
                    />
                    <Box sx={{ display: "flex", gap: 1, mt: 2, mb: 1 }}>
                        <FormControl sx={{ width: "140px" }}>
                            <InputLabel>Code</InputLabel>
                            <Select
                                value={formData.phoneCode}
                                onChange={handleChange("phoneCode")}
                                label="Code"
                            >
                                {Object.values(COUNTRY_PHONE_PREFIXES)
                                    .sort((a, b) => a.country.localeCompare(b.country))
                                    .map((entry) => (
                                        <MenuItem key={entry.country} value={entry.phone}>
                                            {entry.phone} ({entry.country})
                                        </MenuItem>
                                    ))}
                            </Select>
                        </FormControl>
                        <TextField
                            fullWidth
                            label="Phone Number"
                            value={formData.phoneNumber}
                            onChange={(e) => {
                                const val = e.target.value.replace(/\D/g, "");
                                setFormData((prev) => ({ ...prev, phoneNumber: val }));
                            }}
                            placeholder="12345678"
                            autoComplete="off"
                        />
                    </Box>
                    <TextField
                        fullWidth
                        label="Country"
                        value={formData.country}
                        onChange={handleChange("country")}
                        margin="normal"
                        autoComplete="country-name"
                    />
                    <Box sx={{ mt: 2, mb: 2 }}>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                            Company Logo (Optional)
                        </Typography>
                        <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 1 }}>
                            Rectangular landscape logo recommended (e.g. 300x100px). Max 2 MB.
                        </Typography>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 2, flexWrap: "wrap" }}>
                            {logoDisplayUrl && (
                                <Box
                                    sx={{
                                        maxWidth: 200,
                                        border: "1px solid",
                                        borderColor: "divider",
                                        bgcolor: "grey.100",
                                        borderRadius: 1,
                                        p: 0.5,
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                    }}
                                >
                                    <img
                                        src={logoDisplayUrl}
                                        alt="Logo preview"
                                        style={{
                                            maxWidth: "100%",
                                            maxHeight: 100,
                                            width: "auto",
                                            height: "auto",
                                            display: "block",
                                            objectFit: "contain",
                                        }}
                                        onError={() => {}}
                                    />
                                </Box>
                            )}
                            <Box>
                                <Button
                                    component="label"
                                    variant="outlined"
                                    startIcon={<CloudUploadIcon />}
                                    size="small"
                                    sx={{ textTransform: "none" }}
                                >
                                    {logoFileName || "Choose file"}
                                    <input
                                        type="file"
                                        hidden
                                        accept="image/*"
                                        onChange={handleLogoChange}
                                    />
                                </Button>
                                {logoFileName && (
                                    <Typography variant="caption" display="block" sx={{ mt: 0.5 }} color="text.secondary">
                                        {logoFileName}
                                    </Typography>
                                )}
                            </Box>
                        </Box>
                    </Box>
                    <Button
                        type="submit"
                        variant="contained"
                        color="primary"
                        fullWidth
                        size="large"
                        disabled={saving}
                        sx={{
                            py: 1.5,
                            mt: 1,
                            fontSize: "1rem",
                            textTransform: "none",
                            fontWeight: 600,
                        }}
                    >
                        {saving ? (
                            <>
                                <CircularProgress size={22} sx={{ mr: 1 }} color="inherit" />
                                Creating...
                            </>
                        ) : (
                            "Create Account"
                        )}
                    </Button>
                </Box>
            </CardContent>
        </Card>
    );
};

export default CompleteRegistrationForm;
